import * as THREE from "three";
import type { Cup } from "./types";

const loader = new THREE.TextureLoader();
loader.setCrossOrigin("anonymous");

const cache = new Map<string, THREE.Texture>();

const PLACEHOLDER_W = 1024;
const PLACEHOLDER_H = 512;

/**
 * Resolve the body-wrap texture for a cup. When `cup.textureUrl` is set
 * we expect a pre-stitched cylindrical wrap (produced by
 * `scripts/build/stitch-cup-texture.mjs`) — load it as-is, no per-cup canvas
 * compositing. When the URL is absent we generate a placeholder canvas
 * with the city name centered on the cup's body color.
 *
 * The lathe geometry (see `cup.ts`) places its seam at the cup's back, so
 * the stitched texture's center column (canvas U=0.5) lands at the
 * camera-facing front and any U=0/1 mismatch hides at the back.
 */
export function getCupTexture(cup: Cup): THREE.Texture {
  if (cup.textureUrl) {
    const cached = cache.get(cup.textureUrl);
    if (cached) return cached;
    const tex = loader.load(cup.textureUrl);
    configureWrap(tex);
    cache.set(cup.textureUrl, tex);
    return tex;
  }

  const placeholderKey = `placeholder:${cup.id}`;
  const cached = cache.get(placeholderKey);
  if (cached) return cached;
  const tex = createPlaceholderTexture(cup);
  configureWrap(tex);
  cache.set(placeholderKey, tex);
  return tex;
}

function configureWrap(tex: THREE.Texture): void {
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
}

function createPlaceholderTexture(cup: Cup): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = PLACEHOLDER_W;
  canvas.height = PLACEHOLDER_H;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = cup.bodyColor;
  ctx.fillRect(0, 0, PLACEHOLDER_W, PLACEHOLDER_H);

  const grad = ctx.createLinearGradient(0, 0, 0, PLACEHOLDER_H);
  grad.addColorStop(0, "rgba(255,255,255,0.08)");
  grad.addColorStop(0.5, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.20)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, PLACEHOLDER_W, PLACEHOLDER_H);

  if (cup.accentColor) {
    ctx.fillStyle = cup.accentColor;
    ctx.fillRect(0, 28, PLACEHOLDER_W, 6);
    ctx.fillRect(0, PLACEHOLDER_H - 56, PLACEHOLDER_W, 3);
  }

  ctx.fillStyle = cup.accentColor ?? "#ffffff";
  ctx.font =
    "600 72px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText(cup.city.toUpperCase(), PLACEHOLDER_W / 2, PLACEHOLDER_H / 2);

  ctx.font =
    "500 24px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto";
  ctx.fillStyle = "rgba(255,255,255,0.78)";
  ctx.fillText(
    cup.series.toUpperCase(),
    PLACEHOLDER_W / 2,
    PLACEHOLDER_H / 2 + 60,
  );

  return new THREE.CanvasTexture(canvas);
}
